import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '@/utils/api';
import { useBadgeChecker } from '@/hooks/useBadgeChecker';
import confetti from 'canvas-confetti';

// Educational content about savings funds
const EMERGENCY_FUND_INFO = {
  what: {
    title: "What is a Savings Stash?",
    icon: "💰",
    content: "A savings stash is money you set aside for both planned goals and unexpected expenses. It's your financial foundation that helps you achieve your dreams while staying prepared for life's surprises."
  },
  why: {
    title: "Why is Saving Important?",
    icon: "⚡",
    points: [
      "Reach your financial goals faster",
      "Be prepared for unexpected opportunities",
      "Cover surprise expenses without stress",
      "Avoid relying on credit cards or loans",
      "Build wealth and financial freedom",
      "Gain peace of mind and confidence"
    ]
  },
  what_to_include: {
    title: "What Should Be Covered?",
    icon: "📋",
    categories: [
      { name: "Housing", items: ["Rent/Mortgage", "Utilities", "Insurance"] },
      { name: "Transportation", items: ["Car Payment", "Gas", "Insurance"] },
      { name: "Food", items: ["Groceries", "Essential dining"] },
      { name: "Healthcare", items: ["Insurance premiums", "Medications"] },
      { name: "Debt Payments", items: ["Minimum payments on loans"] },
      { name: "Communication", items: ["Phone", "Internet"] }
    ]
  },
  how_to_save: {
    title: "How to Build Your Savings",
    icon: "🚀",
    steps: [
      { step: 1, title: "Start Small", desc: "Begin with $500-$1,000 as a starter fund" },
      { step: 2, title: "Automate", desc: "Set up automatic transfers each payday" },
      { step: 3, title: "Use Windfalls", desc: "Add tax refunds, bonuses, or gifts" },
      { step: 4, title: "Cut & Save", desc: "Reduce one expense and save the difference" },
      { step: 5, title: "Keep it Separate", desc: "Use a separate savings account" }
    ]
  },
  when_to_use: {
    title: "When to Use Your Savings",
    icon: "🎯",
    use: ["Planned purchases and goals", "Investment opportunities", "Essential expenses", "True emergencies when needed"],
    dont_use: ["Impulse purchases", "Regular bills you forgot about", "Sales or shopping deals", "Non-essential items"]
  }
};

// Milestone thresholds for celebrations
const MILESTONES = [
  { percent: 25, message: "Quarter way there!", emoji: "🌱" },
  { percent: 50, message: "Halfway to your goal!", emoji: "⚡" },
  { percent: 75, message: "Almost there!", emoji: "🔥" },
  { percent: 100, message: "Goal achieved!", emoji: "🎉" },
];

function EmergencyFund() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [contributionAmount, setContributionAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEducation, setShowEducation] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [history, setHistory] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');
  
  // Badge checker hook
  const { checkBadges } = useBadgeChecker();

  const loadData = useCallback(async () => {
    try {
      const response = await apiGet('/emergency-fund');
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        
        // Check for milestone celebrations
        const progress = result.calculations?.progress_percent || 0;
        const prevProgress = data?.calculations?.progress_percent || 0;
        
        for (const milestone of MILESTONES) {
          if (progress >= milestone.percent && prevProgress < milestone.percent) {
            triggerCelebration(milestone);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error loading emergency fund:', error);
    } finally {
      setLoading(false);
    }
  }, [data?.calculations?.progress_percent]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await apiGet('/emergency-fund/history');
      
      if (response.ok) {
        const result = await response.json();
        setHistory(result);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadHistory();
  }, []);

  const triggerCelebration = (milestone) => {
    setCelebrationMessage(`${milestone.emoji} ${milestone.message}`);
    setShowCelebration(true);
    
    // Confetti animation
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
    
    setTimeout(() => setShowCelebration(false), 4000);
  };

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await apiPut('/emergency-fund', updates);
      
      if (response.ok) {
        await loadData();
        
        // Check for new badges (Emergency Fund Starter badge)
        if (updates.is_active) {
          checkBadges();
        }
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleContribute = async () => {
    const amount = parseFloat(contributionAmount);
    if (!amount || amount <= 0) return;
    
    try {
      const response = await apiPost(`/emergency-fund/contribute?amount=${amount}`, {});
      
      if (response.ok) {
        setShowContributeModal(false);
        setContributionAmount('');
        await loadData();
        await loadHistory();
        
        // Check for new badges after contributing
        checkBadges();
      }
    } catch (error) {
      console.error('Error contributing:', error);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) return;
    
    try {
      const response = await apiPost(`/emergency-fund/withdraw?amount=${amount}&reason=${encodeURIComponent(withdrawReason)}`, {});
      
      if (response.ok) {
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        setWithdrawReason('');
        await loadData();
        await loadHistory();
      }
    } catch (error) {
      console.error('Error withdrawing:', error);
    }
  };

  const toggleBillInclusion = async (billId, include) => {
    try {
      await apiPut(`/emergency-fund/bill/${billId}`, { include_in_emergency_fund: include });
      await loadData();
    } catch (error) {
      console.error('Error updating bill:', error);
    }
  };

  // Quick add presets
  const quickAddAmounts = [25, 50, 100, 250, 500];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="emergency-fund-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const { settings, calculations, bills } = data || { settings: {}, calculations: {}, bills: { included: [], excluded: [] } };
  const isActive = settings?.is_active || false;

  // Calculate progress ring
  const progressPercent = Math.min(calculations.progress_percent || 0, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="emergency-fund-page">
      {/* Celebration Toast */}
      {showCelebration && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-bounce">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-full shadow-lg text-lg font-bold">
            {celebrationMessage}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="savings-stash-title">
              💰 Savings Stash
            </h1>
            <p className="text-emerald-100 mt-1">
              Build your savings, secure your future
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEducation(!showEducation)}
              data-testid="toggle-guide-btn"
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
            >
              {showEducation ? 'Hide' : 'Show'} Guide
            </button>
            {!isActive && (
              <button
                onClick={() => updateSettings({ is_active: true })}
                data-testid="start-fund-btn"
                className="px-4 py-2 bg-white text-emerald-600 rounded-lg font-medium hover:bg-emerald-50 transition-colors"
              >
                Start Saving 🚀
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Educational Content - Collapsible */}
      {showEducation && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200" data-testid="education-section">
          <h2 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
            📚 Savings Guide
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* What is it */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                {EMERGENCY_FUND_INFO.what.icon} {EMERGENCY_FUND_INFO.what.title}
              </h3>
              <p className="text-gray-600 text-sm">{EMERGENCY_FUND_INFO.what.content}</p>
            </div>
            
            {/* Why important */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                {EMERGENCY_FUND_INFO.why.icon} {EMERGENCY_FUND_INFO.why.title}
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                {EMERGENCY_FUND_INFO.why.points.slice(0, 4).map((point, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-500">✓</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* What to include */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                {EMERGENCY_FUND_INFO.what_to_include.icon} {EMERGENCY_FUND_INFO.what_to_include.title}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {EMERGENCY_FUND_INFO.what_to_include.categories.map((cat, i) => (
                  <div key={i}>
                    <span className="font-medium text-gray-800">{cat.name}:</span>
                    <span className="text-gray-600 ml-1">{cat.items[0]}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* How to save */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                {EMERGENCY_FUND_INFO.how_to_save.icon} {EMERGENCY_FUND_INFO.how_to_save.title}
              </h3>
              <div className="space-y-2">
                {EMERGENCY_FUND_INFO.how_to_save.steps.slice(0, 3).map((step) => (
                  <div key={step.step} className="flex items-center gap-2 text-sm">
                    <span className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">
                      {step.step}
                    </span>
                    <span className="font-medium text-gray-800">{step.title}</span>
                    <span className="text-gray-500">- {step.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* When to use */}
          <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
              {EMERGENCY_FUND_INFO.when_to_use.icon} {EMERGENCY_FUND_INFO.when_to_use.title}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-green-700 mb-2">✅ Use For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {EMERGENCY_FUND_INFO.when_to_use.use.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">❌ Don't Use For:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {EMERGENCY_FUND_INFO.when_to_use.dont_use.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {isActive ? (
        <>
          {/* Progress Card with Ring */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-testid="progress-card">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Progress Ring */}
              <div className="flex flex-col items-center">
                <div className="relative w-40 h-40">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="45"
                      stroke="#e5e7eb"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="45"
                      stroke="url(#gradient)"
                      strokeWidth="10"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      className="transition-all duration-1000"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#14b8a6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900">{progressPercent.toFixed(0)}%</span>
                    <span className="text-sm text-gray-500">Complete</span>
                  </div>
                </div>
                <p className="text-center mt-2 text-gray-600">
                  {MILESTONES.find(m => progressPercent < m.percent)?.emoji || '🎉'} 
                  {progressPercent < 25 ? " Just getting started!" :
                   progressPercent < 50 ? " Making progress!" :
                   progressPercent < 75 ? " Over halfway there!" :
                   progressPercent < 100 ? " Almost at your goal!" :
                   " Goal achieved!"}
                </p>
              </div>

              {/* Stats and Actions */}
              <div className="flex-1">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Your Emergency Fund</h2>
                    <p className="text-gray-600">
                      Target: {settings.target_months} months • ${(calculations.target_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowContributeModal(true)}
                      data-testid="add-funds-btn"
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                    >
                      + Add Funds
                    </button>
                    <button
                      onClick={() => setShowWithdrawModal(true)}
                      data-testid="withdraw-btn"
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                    >
                      Withdraw
                    </button>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-700 mb-1">Current Balance</p>
                    <p className="text-xl font-bold text-emerald-900" data-testid="current-balance">
                      ${(settings.current_balance || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-700 mb-1">Monthly Expenses</p>
                    <p className="text-xl font-bold text-blue-900">
                      ${(calculations.monthly_expenses || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-purple-700 mb-1">Remaining</p>
                    <p className="text-xl font-bold text-purple-900">
                      ${(calculations.remaining || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-amber-700 mb-1">Months Covered</p>
                    <p className="text-xl font-bold text-amber-900">
                      {calculations.monthly_expenses > 0 
                        ? ((settings.current_balance || 0) / calculations.monthly_expenses).toFixed(1)
                        : '0'} mo
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs: Settings / History / Bills */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {[
                  { id: 'overview', label: 'Settings', icon: '⚙️' },
                  { id: 'history', label: 'History', icon: '📜' },
                  { id: 'bills', label: 'Included Bills', icon: '📋' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`flex-1 py-3 px-4 text-center border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-emerald-500 text-emerald-600 bg-emerald-50/50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Settings Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6" data-testid="settings-tab">
                  {/* Target Months */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Target Emergency Fund
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => updateSettings({ target_months: 3 })}
                        disabled={saving}
                        data-testid="target-3-months"
                        className={`flex-1 py-4 rounded-xl font-medium transition-all ${
                          settings.target_months === 3
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-lg">3 Months</span>
                        <br />
                        <span className="text-sm opacity-80">${(calculations.target_3_month || 0).toLocaleString()}</span>
                      </button>
                      <button
                        onClick={() => updateSettings({ target_months: 6 })}
                        disabled={saving}
                        data-testid="target-6-months"
                        className={`flex-1 py-4 rounded-xl font-medium transition-all ${
                          settings.target_months === 6
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-lg">6 Months</span>
                        <br />
                        <span className="text-sm opacity-80">${(calculations.target_6_month || 0).toLocaleString()}</span>
                      </button>
                    </div>
                  </div>

                  {/* Monthly Contribution Goal */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Contribution Goal
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        value={settings.monthly_contribution || 0}
                        onChange={(e) => updateSettings({ monthly_contribution: parseFloat(e.target.value) || 0 })}
                        data-testid="monthly-contribution-input"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <span className="text-gray-500">/month</span>
                    </div>
                    {settings.monthly_contribution > 0 && calculations.remaining > 0 && (
                      <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                        <span>🎯</span>
                        At this rate, you'll reach your goal in ~{Math.ceil(calculations.remaining / settings.monthly_contribution)} months
                      </p>
                    )}
                  </div>

                  {/* Deactivate */}
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => updateSettings({ is_active: false })}
                      data-testid="deactivate-btn"
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Deactivate Emergency Fund
                    </button>
                  </div>
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div data-testid="history-tab">
                  {history.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {history.map((item, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            item.amount > 0 ? 'bg-emerald-50' : 'bg-red-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`text-2xl ${item.amount > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {item.amount > 0 ? '💰' : '💸'}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.amount > 0 ? 'Contribution' : 'Withdrawal'}
                                {item.reason && <span className="text-gray-500 font-normal"> - {item.reason}</span>}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(item.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${item.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {item.amount > 0 ? '+' : ''}${Math.abs(item.amount).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500">
                              Balance: ${(item.balance_after || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <span className="text-4xl mb-3 block">📊</span>
                      <p>No transactions yet</p>
                      <p className="text-sm">Add your first contribution to get started!</p>
                    </div>
                  )}
                </div>
              )}

              {/* Bills Tab */}
              {activeTab === 'bills' && (
                <div data-testid="bills-tab">
                  {/* Target Summary */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 mb-4 border border-emerald-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <div>
                        <p className="text-sm font-medium text-emerald-800">
                          {settings.target_months}-Month Target: <span className="text-lg font-bold">${(calculations.target_amount || 0).toLocaleString()}</span>
                        </p>
                        <p className="text-xs text-emerald-600">
                          Based on ${(calculations.monthly_expenses || 0).toLocaleString()}/mo from {bills.included?.length || 0} bills
                        </p>
                      </div>
                      <a
                        href="/bills"
                        className="text-sm text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-1"
                      >
                        <span>📝</span> Manage Bills →
                      </a>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    Toggle bills to include or exclude them from your emergency fund calculation. 
                    Changes update your target in real-time.
                  </p>
                  
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {[...bills.included, ...bills.excluded].map((bill) => (
                      <label
                        key={bill.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border transition-colors ${
                          bill.include_in_emergency_fund !== false 
                            ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={bill.include_in_emergency_fund !== false}
                            onChange={(e) => toggleBillInclusion(bill.id, e.target.checked)}
                            className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                          />
                          <div>
                            <span className={`font-medium ${bill.include_in_emergency_fund !== false ? 'text-gray-900' : 'text-gray-500'}`}>
                              {bill.name}
                            </span>
                            <span className="ml-2 text-xs text-gray-500 bg-white px-2 py-0.5 rounded">
                              {bill.category}
                            </span>
                          </div>
                        </div>
                        <span className={`font-medium ${bill.include_in_emergency_fund !== false ? 'text-emerald-700' : 'text-gray-400'}`}>
                          ${(bill.monthly_amount || 0).toFixed(2)}/mo
                        </span>
                      </label>
                    ))}
                    
                    {bills.included.length === 0 && bills.excluded.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <span className="text-4xl mb-3 block">📝</span>
                        <p>No bills found</p>
                        <p className="text-sm mb-4">Add bills to include them in your emergency fund calculation</p>
                        <a
                          href="/bills"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                        >
                          <span>+</span> Add Bills
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Tip */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">💡 Tip:</span> You can also toggle emergency fund inclusion from the Bills page using the 🛡️ shield icon on each bill.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Not Started Yet - Onboarding */
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center" data-testid="onboarding-card">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-5xl">🛡️</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Start Building Your Safety Net</h2>
            <p className="text-gray-600 mb-6">
              Based on your bills, we recommend saving{' '}
              <strong className="text-emerald-600">${(calculations.target_3_month || 0).toLocaleString()}</strong> (3 months) or{' '}
              <strong className="text-emerald-600">${(calculations.target_6_month || 0).toLocaleString()}</strong> (6 months) for emergencies.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => updateSettings({ is_active: true, target_months: 3 })}
                data-testid="start-3-month-btn"
                className="py-5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg shadow-emerald-200"
              >
                <span className="text-lg block">3 Month Fund</span>
                <span className="text-sm opacity-90">${(calculations.target_3_month || 0).toLocaleString()}</span>
              </button>
              <button
                onClick={() => updateSettings({ is_active: true, target_months: 6 })}
                data-testid="start-6-month-btn"
                className="py-5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-medium hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg shadow-teal-200"
              >
                <span className="text-lg block">6 Month Fund</span>
                <span className="text-sm opacity-90">${(calculations.target_6_month || 0).toLocaleString()}</span>
              </button>
            </div>
            
            <p className="text-sm text-gray-500">
              📋 Based on {bills.included?.length || 0} bills totaling ${(calculations.monthly_expenses || 0).toLocaleString()}/month
            </p>
          </div>
        </div>
      )}

      {/* Contribute Modal */}
      {showContributeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="contribute-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              💰 Add to Emergency Fund
            </h3>
            
            {/* Quick Add Buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              {quickAddAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setContributionAmount(amount.toString())}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    contributionAmount === amount.toString()
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Custom Amount</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-lg">$</span>
                <input
                  type="number"
                  value={contributionAmount}
                  onChange={(e) => setContributionAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="contribution-amount-input"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowContributeModal(false);
                  setContributionAmount('');
                }}
                className="flex-1 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleContribute}
                disabled={!contributionAmount || parseFloat(contributionAmount) <= 0}
                data-testid="confirm-contribute-btn"
                className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Add ${parseFloat(contributionAmount || 0).toLocaleString()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="withdraw-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              💸 Withdraw from Emergency Fund
            </h3>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Remember:</strong> Only use your emergency fund for true emergencies like job loss, medical bills, or essential repairs.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-lg">$</span>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  max={settings.current_balance}
                  placeholder="0.00"
                  data-testid="withdraw-amount-input"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500"
                  autoFocus
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Available: ${(settings.current_balance || 0).toLocaleString()}
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason (helps you track)</label>
              <input
                type="text"
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                placeholder="e.g., Car repair, Medical bill, Job loss"
                data-testid="withdraw-reason-input"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowWithdrawModal(false);
                  setWithdrawAmount('');
                  setWithdrawReason('');
                }}
                className="flex-1 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdraw}
                disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > (settings.current_balance || 0)}
                data-testid="confirm-withdraw-btn"
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Withdraw ${parseFloat(withdrawAmount || 0).toLocaleString()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmergencyFund;
